import { FileOutlined } from '@ant-design/icons';
import { uploadedOrderFileUrl } from '../config/mediaPaths';
import { parseSelectionSummaryBlocks } from './SelectionSummaryDisplay';
import styles from './CustomisationValuesDisplay.module.css';

function getFileList(value) {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch {
    /* not json */
  }
  return [value].filter(Boolean);
}

function fileNameFromKey(key) {
  const name = String(key).split('/').pop() || String(key);
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

function isImageFile(key) {
  return /\.(avif|gif|jpe?g|png|webp)$/i.test(fileNameFromKey(key));
}

function normalizeLabel(value) {
  return String(value || '').trim().toLowerCase();
}

function summaryValueForLabel(selectionSummary, label) {
  if (!label || !selectionSummary) return '';
  const blocks = parseSelectionSummaryBlocks(selectionSummary);
  const match = blocks.find((block) => normalizeLabel(block.label) === normalizeLabel(label));
  return match?.value ? String(match.value).trim() : '';
}

function looksLikeInternalId(value, customisationId) {
  const text = String(value || '').trim();
  if (!text) return true;
  if (customisationId && text === String(customisationId).trim()) return true;
  if (text.includes(' ') || text.includes('\n')) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text)) {
    return true;
  }
  return /^[a-zA-Z0-9_-]{12,}$/.test(text);
}

function resolveDisplayValue(entry, selectionSummary) {
  const summaryValue = summaryValueForLabel(selectionSummary, entry.name);
  if (summaryValue) return summaryValue;

  const rawValue = String(entry.value || '').trim();
  if (!rawValue) return '';

  if (entry.type === 'dropdown' && looksLikeInternalId(rawValue, entry.customisationId)) {
    return '';
  }

  return rawValue;
}

function shouldSkipEntry(entry, selectionSummary) {
  if (!entry?.selected) return true;
  if (entry.type === 'file') return false;

  const summaryValue = summaryValueForLabel(selectionSummary, entry.name);
  if (summaryValue) return true;

  if (entry.type === 'boolean') return false;

  const displayValue = resolveDisplayValue(entry, selectionSummary);
  return !displayValue;
}

export default function CustomisationValuesDisplay({
  values,
  filesDeleted = false,
  orderId,
  hubId,
  bizId,
  selectionSummary = '',
}) {
  if (!values || !Array.isArray(values) || values.length === 0) return null;

  const rows = values
    .map((entry, idx) => {
      if (shouldSkipEntry(entry, selectionSummary)) return null;

      const { name, type } = entry;

      if (type === 'boolean') {
        return (
          <div key={idx} className={styles.row}>
            <span className={styles.label}>{name || 'Add-on'}</span>
            <span className={styles.booleanValue}>Yes</span>
          </div>
        );
      }

      if (type === 'file') {
        const files = getFileList(entry.value);
        if (files.length === 0) return null;
        return (
          <div key={idx} className={`${styles.row} ${styles.fileRow}`}>
            <span className={styles.label}>{name || 'File'}</span>
            {filesDeleted ? (
              <span className={styles.deleted}>Files removed after the order was closed</span>
            ) : (
              <div className={styles.files}>
                {files.map((fileKey) => {
                  const fileUrl = uploadedOrderFileUrl({
                    orderId,
                    hubId,
                    bizId,
                    fileName: fileKey,
                  });
                  if (!fileUrl) return null;
                  return (
                    <a
                      key={fileKey}
                      href={fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.fileChip}
                    >
                      {isImageFile(fileKey) ? (
                        <img src={fileUrl} alt={fileNameFromKey(fileKey)} className={styles.fileChipThumb} />
                      ) : (
                        <FileOutlined className={styles.fileIcon} />
                      )}
                      <span className={styles.fileChipName}>{fileNameFromKey(fileKey)}</span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      const displayValue = resolveDisplayValue(entry, selectionSummary);
      if (!displayValue) return null;

      return (
        <div key={idx} className={styles.row}>
          <span className={styles.label}>{name || type}</span>
          <span className={styles.value}>{displayValue}</span>
        </div>
      );
    })
    .filter(Boolean);

  if (rows.length === 0) return null;

  return <div className={styles.root}>{rows}</div>;
}
